import { UserTier, TierDefinition } from "./types";

export const TIER_DEFINITIONS: Record<UserTier, TierDefinition> = {
  pilot: {
    id: 'pilot',
    name: 'Pilot',
    sortOrder: 0,
    isActive: true,
    isInMainPricingTable: false,
    pricing: {
      monthlyPrice: 19900,           // $199.00
      annualPrice: 29900,           // $299.00 (~17% discount)
    },
    marketingDescription: 'Pilot program for early adopters with exclusive benefits',
    databaseQuotas: {
      connectors: 20,
      chatMessages: 50000,
      additionalUsers: 10,
      fileUploadCount: 20,
      fileUploadTotalStorage: 200,  // MB
    },
    monthlyQuotas: {
      aiCredits: 1000,                // ~200 chatbot replies or 10 summaries or mix
      messagesPerMonth: 5000,          // incoming messages (pilot)
      automationsPerMonth: 1250,
    },
    featureFlags: {
      chatbot: {
        moderationEnabled: false,
        proactiveModeEnabled: true,
        reactionsEnabled: true,
        welcomeNewMembersEnabled: false,
      },
    },
  },
  t1: {
    id: 't1',
    name: 'Gather',
    sortOrder: 1,
    isActive: true,
    isInMainPricingTable: true,
    pricing: {
      monthlyPrice: 9900,
      annualPrice: 107000,
    },
    marketingDescription: 'For smaller or newer communities',
    databaseQuotas: {
      connectors: 3,
      chatMessages: 100000,
      additionalUsers: 0,
      fileUploadCount: 20,
      fileUploadTotalStorage: 200,  // MB
    },
    monthlyQuotas: {
      aiCredits: 500,                // ~50 chatbot replies or 10 summaries or mix
      messagesPerMonth: 1000,       // Cap on incoming messages per calendar month
      automationsPerMonth: 250,
    },
    featureFlags: {
      chatbot: {
        moderationEnabled: false,
        proactiveModeEnabled: false,
        reactionsEnabled: true,
        welcomeNewMembersEnabled: true,
      },
    },
  },
  t2: {
    id: 't2',
    name: 'Grow',
    sortOrder: 2,
    isActive: true,
    isInMainPricingTable: true,
    pricing: {
      monthlyPrice: 29900,
      annualPrice: 322920,
    },
    marketingDescription: 'For established communities with growing needs',
    databaseQuotas: {
      connectors: 20,
      chatMessages: 100000,
      additionalUsers: 5,
      fileUploadCount: 100,
      fileUploadTotalStorage: 500,  // MB
    },
    monthlyQuotas: {
      aiCredits: 2500,
      messagesPerMonth: 4000,
      automationsPerMonth: 1000,
    },
    featureFlags: {
      chatbot: {
        moderationEnabled: false,
        proactiveModeEnabled: true,
        reactionsEnabled: true,
        welcomeNewMembersEnabled: true,
      },
    },
  },
  exampleCustom: {
    id: 'exampleCustom',
    name: 'Scale',
    sortOrder: 3,
    isActive: true,
    isInMainPricingTable: true,
    pricing: {
      monthlyPrice: -1,
      annualPrice: -1,
    },
    marketingDescription: 'For larger communities with advanced needs',
    databaseQuotas: {
      connectors: -1,
      chatMessages: -1,
      additionalUsers: -1,
      fileUploadCount: 500,
      fileUploadTotalStorage: 2500,  // MB
    },
    monthlyQuotas: {
      aiCredits: 50000,
      messagesPerMonth: 250000,
      automationsPerMonth: 62500,
    },
    featureFlags: {
      chatbot: {
        moderationEnabled: false,
        proactiveModeEnabled: true,
        reactionsEnabled: true,
        welcomeNewMembersEnabled: true,
      },
    },
  },
  pro: {
    id: 'pro',
    name: 'Pro',
    sortOrder: 3,
    isActive: true,
    isInMainPricingTable: false,
    pricing: {
      monthlyPrice: -1,
      annualPrice: -1,
    },
    marketingDescription: 'A custom negotiated plan.',
    databaseQuotas: {
      connectors: 50,
      chatMessages: 30000,
      additionalUsers: 20,
      fileUploadCount: 500,
      fileUploadTotalStorage: 2500,  // MB
    },
    monthlyQuotas: {
      aiCredits: 500,
      messagesPerMonth: 5000,
      automationsPerMonth: 1250,
    },
    featureFlags: {
      chatbot: {
        moderationEnabled: false,
        proactiveModeEnabled: true,
        reactionsEnabled: true,
        welcomeNewMembersEnabled: true,
      },
    },
  },
  customCircleUp: {
    id: 'customCircleUp',
    name: 'Custom (CU)',
    sortOrder: 2,
    isActive: true,
    isInMainPricingTable: false,
    pricing: {
      monthlyPrice: 29900,
      annualPrice: 322920,
    },
    marketingDescription: 'Custom Plan (CircleUp)',
    databaseQuotas: {
      connectors: 120,
      chatMessages: 250000,
      additionalUsers: 5,
      fileUploadCount: 100,
      fileUploadTotalStorage: 5000,  // 5Gb
    },
    monthlyQuotas: {
      aiCredits: 5000,
      messagesPerMonth: 15000,
      automationsPerMonth: 5000,
    },
    featureFlags: {
      chatbot: {
        moderationEnabled: true,
        proactiveModeEnabled: true,
        reactionsEnabled: true,
        welcomeNewMembersEnabled: true,
      },
    },
  },
  custom: {
    id: 'custom',
    name: 'Custom',
    sortOrder: 4,
    isActive: true,
    isInMainPricingTable: false,
    pricing: {
      monthlyPrice: 0,              // Contact sales
      annualPrice: 0,               // Contact sales
    },
    marketingDescription: 'Enterprise-grade solution with unlimited resources',
    databaseQuotas: {
      connectors: -1,               // Unlimited
      chatMessages: -1,             // Unlimited
      additionalUsers: -1,          // Unlimited
      fileUploadCount: 2000,
      fileUploadTotalStorage: 10000,  // MB
    },
    monthlyQuotas: {
      aiCredits: -1,                // Unlimited
      messagesPerMonth: 5000,         // Unlimited
      automationsPerMonth: 1250,
    },
    featureFlags: {
      chatbot: {
        moderationEnabled: false,
        proactiveModeEnabled: true,
        reactionsEnabled: true,
        welcomeNewMembersEnabled: true,
      },
    },
  },
};

export function getTierDefinition(tier: UserTier): TierDefinition {
  return TIER_DEFINITIONS[tier];
}
